import apiClient, { ApiResponse } from '../api/client';

export interface RevenueReportFilters {
  start_date: string;
  end_date: string;
  service_id?: number;
  zone_id?: number;
  ward_id?: number;
  subcounty_id?: number;
  format?: 'pdf' | 'excel' | 'csv';
}

export interface TransactionReportFilters {
  start_date: string;
  end_date: string;
  status?: string;
  payment_method?: string;
  customer_id?: number;
  service_id?: number;
  format?: 'pdf' | 'excel' | 'csv';
}

export interface CustomerReportFilters {
  status?: 'active' | 'inactive';
  has_outstanding?: boolean;
  service_id?: number;
  zone_id?: number;
  format?: 'pdf' | 'excel' | 'csv';
}

export interface RevenueReportData {
  period: { start: string; end: string };
  total_revenue: number;
  total_transactions: number;
  average_revenue?: number;
  growth_rate?: number;
  by_service: Array<{
    service_id: number;
    service_name: string;
    revenue: number;
    transactions: number;
    percentage?: number;
    average_per_transaction?: number;
  }>;
  by_touchpoint?: Array<{
    touchpoint_id: number;
    touchpoint: string;
    revenue: number;
    transactions: number;
    percentage?: number;
  }>;
  by_location?: Array<{
    location_id: number;
    location_name: string;
    revenue: number;
    transactions: number;
  }>;
  by_payment_method: Array<{
    method: string;
    revenue: number;
    transactions: number;
    percentage?: number;
  }>;
  daily_trend?: Array<{
    date: string;
    revenue: number;
    count: number;
  }>;
  /** Wallet money held on behalf of customers — a liability, not revenue. */
  wallet_obligation?: WalletObligation;
}

export interface WalletObligation {
  topped_up: number;
  spent: number;
  outstanding: number;
}

export interface WalletReportData {
  period: { start: string | null; end: string | null };
  obligation: WalletObligation;
  period_summary: {
    topped_up: number;
    spent: number;
    net_change: number;
    topup_count: number;
    spend_count: number;
  };
  daily_trend: Array<{ date: string; topped_up: number; spent: number }>;
  recent_activity: Array<{
    id: number;
    reference: string;
    type: 'top_up' | 'spend';
    amount: number;
    customer: string;
    date: string;
  }>;
}

export interface TransactionReportData {
  period: { start: string; end: string };
  total_transactions: number;
  total_amount: number;
  by_status: Record<string, { count: number; amount: number }>;
  by_payment_method: Record<string, { count: number; amount: number }>;
  transactions: Array<any>;
}

export interface CustomerReportData {
  total_customers: number;
  active_customers: number;
  inactive_customers: number;
  customers_with_outstanding: number;
  total_outstanding: number;
  by_service?: Array<{
    service_id: number;
    service_name: string;
    customer_count: number;
  }>;
  customers: Array<any>;
}

class ReportService {
  private baseUrl = '/admin/county/reports';

  // Revenue Reports
  async getRevenueReport(filters: RevenueReportFilters): Promise<ApiResponse<RevenueReportData>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    return apiClient.get<RevenueReportData>(`${this.baseUrl}/revenue?${params.toString()}`);
  }

  async downloadRevenueReport(filters: RevenueReportFilters): Promise<Blob> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    const response = await apiClient.get(`${this.baseUrl}/revenue/download?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data as Blob;
  }

  // Wallet Report
  async getWalletReport(filters: { date_filter?: string; start_date?: string; end_date?: string }): Promise<ApiResponse<WalletReportData>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    const qs = params.toString();
    return apiClient.get<WalletReportData>(`${this.baseUrl}/wallet${qs ? `?${qs}` : ''}`);
  }

  // Transaction Reports
  async getTransactionReport(filters: TransactionReportFilters): Promise<ApiResponse<TransactionReportData>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    return apiClient.get<TransactionReportData>(`${this.baseUrl}/transactions?${params.toString()}`);
  }

  async downloadTransactionReport(filters: TransactionReportFilters): Promise<Blob> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    const response = await apiClient.get(`${this.baseUrl}/transactions/download?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data as Blob;
  }

  // Customer Reports
  async getCustomerReport(filters?: CustomerReportFilters): Promise<ApiResponse<CustomerReportData>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    return apiClient.get<CustomerReportData>(`${this.baseUrl}/customers?${params.toString()}`);
  }

  async downloadCustomerReport(filters?: CustomerReportFilters): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const response = await apiClient.get(`${this.baseUrl}/customers/download?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data as Blob;
  }

  // Service Performance Reports
  async getServicePerformanceReport(startDate: string, endDate: string): Promise<ApiResponse<any>> {
    return apiClient.get(`${this.baseUrl}/service-performance`, {
      params: { start_date: startDate, end_date: endDate }
    });
  }

  // Collection Efficiency Reports
  async getCollectionEfficiencyReport(startDate: string, endDate: string): Promise<ApiResponse<any>> {
    return apiClient.get(`${this.baseUrl}/collection-efficiency`, {
      params: { start_date: startDate, end_date: endDate }
    });
  }

  // Outstanding Balances Report
  async getOutstandingBalancesReport(asOf?: string): Promise<ApiResponse<any>> {
    return apiClient.get(`${this.baseUrl}/outstanding-balances`, {
      params: asOf ? { as_of: asOf } : {}
    });
  }
}

export const reportService = new ReportService();
export default reportService;
